import * as Immutable from 'seamless-immutable';
import {
  ISSUE_ATTRIBUTES_FETCH_API_FULFILLED,
  ISSUE_ATTRIBUTES_DEFINITIONS_FETCH_FINISHED,
  ISSUE_ATTRIBUTES_FETCH_API_REJECTED,
  ISSUE_ATTRIBUTES_FETCH_START,
  ISSUE_ATTRIBUTES_CREATE_API_FULFILLED,
  ISSUE_ATTRIBUTES_RESET_HIGHLIGHTING_ROWS,
  ISSUE_ATTRIBUTES_CREATE_API_REJECTED,
  ISSUE_ATTRIBUTES_CREATE_START,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_START,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_FULFILLED,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_REJECTED,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_FINISHED,
  ISSUE_ATTRIBUTES_EDIT_START,
  ISSUE_ATTRIBUTES_EDIT_END,
  ISSUE_ATTRIBUTES_UPDATE_START,
  ISSUE_ATTRIBUTES_UPDATE_API_FULFILLED,
  ISSUE_ATTRIBUTES_UPDATE_API_REJECTED,
  ISSUE_ATTRIBUTES_DELETE_START,
  ISSUE_ATTRIBUTES_DELETE_API_REJECTED,
  ISSUE_ATTRIBUTES_DELETE_API_FULFILLED,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_FULFILLED,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_START,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_REJECTED,
  SHOW_ISSUE_ATTRIBUTES_MODAL,
  ISSUE_ATTRIBUTES_MAPPINGS_SET
} from '../actions';
import {sortAlphabeticallyByTitle} from '../../utils/sortingHelpers';
import {flattenIssueAttributesResults} from '../../utils/issueAttributes-helpers';

const definitionsInitialState = Immutable.from({
  loading: false,
  error: false,
  isCreating: false,
  isUpdating: false,
  entitiesMap: {},
  issueAttributeIds: [],
  definitionTypesCount: {},
  highlightRowIds: [],
  fetched: false,
  editingId: null,
  modalOpen: false
});

const mappingsInitialState = Immutable.from({
  loading: false,
  error: false,
  isUpdating: false,
  entitiesMap: {},
  mappingIds: [],
  fetched: false
});

const addDefinitionTypeCount = (definitionTypesCount, issueAttributeDefinition) =>
  definitionTypesCount[issueAttributeDefinition.dataType]
    ? definitionTypesCount[issueAttributeDefinition.dataType] + 1
    : 1;

const flattenIssueAttributeDefinition = payload => {
  const issueAttributeIds = [];
  const definitionTypesCount = {};
  const entitiesMap = payload.sort(sortAlphabeticallyByTitle).reduce((acc, issueAttributeDefinition) => {
    acc[issueAttributeDefinition.id] = issueAttributeDefinition;
    issueAttributeIds.push(issueAttributeDefinition.id);
    definitionTypesCount[issueAttributeDefinition.dataType] = addDefinitionTypeCount(
      definitionTypesCount,
      issueAttributeDefinition
    );
    return acc;
  }, {});
  return {
    entitiesMap,
    issueAttributeIds,
    definitionTypesCount
  };
};

const issueAttributesDefinitionsReducer = (state = definitionsInitialState, action) => {
  switch (action.type) {
    case ISSUE_ATTRIBUTES_FETCH_START:
      return action.payload.offset === 0
        ? state.merge({
          loading: true,
          error: false,
          definitionTypesCount: {},
          issueAttributeIds: [],
          entitiesMap: {}
        })
        : state.merge({loading: true, error: false});
    case ISSUE_ATTRIBUTES_FETCH_API_FULFILLED: {
      const {entitiesMap, issueAttributeIds, definitionTypesCount} = flattenIssueAttributeDefinition(
        action.payload.results
      );
      return state.merge({
        fetched: true,
        entitiesMap: {...state.entitiesMap, ...entitiesMap},
        issueAttributeIds: state.issueAttributeIds.concat(issueAttributeIds),
        definitionTypesCount: {...state.definitionTypesCount, definitionTypesCount}
      });
    }
    case ISSUE_ATTRIBUTES_DEFINITIONS_FETCH_FINISHED:
      return state.merge({loading: false});
    case ISSUE_ATTRIBUTES_FETCH_API_REJECTED:
      return state.merge({loading: false, error: true});
    case ISSUE_ATTRIBUTES_CREATE_START:
      return state.merge({isCreating: true});
    case ISSUE_ATTRIBUTES_CREATE_API_FULFILLED: {
      const definitionTypesCount = addDefinitionTypeCount(state.definitionTypesCount, action.payload);
      return state.merge({
        entitiesMap: {...state.entitiesMap, [action.payload.id]: action.payload},
        issueAttributeIds: [...state.issueAttributeIds, action.payload.id],
        definitionTypesCount: {
          ...state.definitionTypesCount,
          [action.payload.dataType]: definitionTypesCount
        },
        highlightRowIds: [action.payload.id],
        isCreating: false
      });
    }
    case ISSUE_ATTRIBUTES_CREATE_API_REJECTED:
      return state.merge({isCreating: false});
    case ISSUE_ATTRIBUTES_UPDATE_START:
      return state.merge({isUpdating: true});
    case ISSUE_ATTRIBUTES_UPDATE_API_FULFILLED:
      return state.setIn(['entitiesMap', action.meta.id], action.payload).merge({isUpdating: false});
    case ISSUE_ATTRIBUTES_UPDATE_API_REJECTED:
      return state.merge({isUpdating: false});
    case ISSUE_ATTRIBUTES_RESET_HIGHLIGHTING_ROWS:
      return state.merge({highlightRowIds: []});
    case ISSUE_ATTRIBUTES_EDIT_START:
      return state.merge({editingId: action.payload});
    case ISSUE_ATTRIBUTES_EDIT_END:
      return state.merge({editingId: null});
    case ISSUE_ATTRIBUTES_DELETE_START:
      return state.merge({isDeleting: true});
    case ISSUE_ATTRIBUTES_DELETE_API_REJECTED:
      return state.merge({isDeleting: false});
    case ISSUE_ATTRIBUTES_DELETE_API_FULFILLED: {
      return state.merge({
        isDeleting: false,
        ...action.payload
      });
    }
    case SHOW_ISSUE_ATTRIBUTES_MODAL:
      return state.merge({modalOpen: action.payload.visible});
    default:
      return state;
  }
};

const issueAttributesMappingsReducer = (state = mappingsInitialState, action) => {
  switch (action.type) {
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_START:
      return action.payload.offset === 0
        ? state.merge({loading: true, error: false, mappingIds: [], entitiesMap: {}})
        : state.merge({loading: true, error: false});
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_FULFILLED: {
      const results = action.payload.results;
      const {ids: mappingIds, entitiesMap} = flattenIssueAttributesResults(results);
      return state.merge({
        fetched: true,
        entitiesMap: {...state.entitiesMap, ...entitiesMap},
        mappingIds: state.mappingIds.concat(mappingIds)
      });
    }
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_REJECTED:
      return state.merge({loading: false, error: true});
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_FINISHED:
      return state.merge({loading: false});
    case ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_START:
      return state.merge({isUpdating: true});
    case ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_FULFILLED: {
      return state.merge({
        isUpdating: false,
        mappingIds: action.payload.mappingIds,
        entitiesMap: action.payload.entitiesMap
      });
    }
    case ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_REJECTED:
      return state.merge({isUpdating: false});
    case ISSUE_ATTRIBUTES_MAPPINGS_SET:
      return state.merge({...action.payload});
    default:
      return state;
  }
};

export default (state = {}, action) => ({
  definitions: issueAttributesDefinitionsReducer(state.definitions, action),
  mappings: issueAttributesMappingsReducer(state.mappings, action)
});
